/**
 * Izdanja aplikacije - L-PFR za Windows (ADR-058) i Android kasa (APK).
 *
 * Ista stranica za oba (`ReleasesPage` + `ReleasesConfig`): isti tok objave,
 * kritičnog i obaveznog ažuriranja; razlikuju se ruta, fajl i posledice.
 *
 * Izdanje se objavljuje odavde umesto ručnim kopiranjem foldera na server:
 * instaler se otprema, a tek kad server proveri SHA-256 i veličinu, upisuje
 * `release.json` i uređaji ga vide. Na serveru je uvek JEDNO izdanje - objava
 * zamenjuje okačeno (i isti build), a kritično i rok zamenjenog prelaze na novo.
 *
 * „Kritično" i „obavezno od" menjaju rad SVIH uređaja svih firmi, pa obe
 * radnje traže potvrdu sa objašnjenjem posledica. Menja samo uloga `admin`;
 * ostali vide pregled.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, RefreshCw, ShieldAlert, Upload, X } from 'lucide-react';
import { api } from '../../api/client';
import { loadSession } from '../../auth/auth';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import { Uputstvo, Pojam } from '../../components/Uputstvo';

type Fajl = { sha256: string; size: number };

type Izdanje = {
  build: number;
  version: string;
  critical: boolean;
  mandatoryAfter: string | null;
  withdrawn: boolean;
  publishedAt: string | null;
  notes: string;
  files: Record<string, Fajl>;
  devices: number;
};

type Uredjaj = {
  id: string;
  name: string;
  tenantId: string;
  tenantName: string;
  build: number | null;
  version: string | null;
  lastSeenAt: string | null;
  mandatoryAfter: string | null;
  required: boolean;
};

type Pregled = {
  current: Izdanje | null;
  releases: Izdanje[];
  latestBuild: number | null;
  latestCriticalBuild: number | null;
  totalDevices: number;
  distribution: Array<{ build: number | null; version: string | null; count: number }>;
  behindCritical: number;
  lagging: Uredjaj[];
};

type Potvrda =
  | { vrsta: 'kriticno'; izdanje: Izdanje }
  | { vrsta: 'obavezno'; izdanje: Izdanje }
  | { vrsta: 'povuci'; izdanje: Izdanje };

const mb = (n: number) => `${(n / 1024 / 1024).toLocaleString('sr-RS', { maximumFractionDigits: 1 })} MB`;
const vreme = (iso: string | null) => (iso ? new Date(iso).toLocaleString('sr-RS', { dateStyle: 'short', timeStyle: 'short' }) : '-');
const greska = (e: any, podrazumevano: string) => e?.data?.message || e?.message || podrazumevano;

/** Šta razlikuje izdanja jedne aplikacije od druge na ovoj stranici. */
export type ReleasesConfig = {
  /** `/admin/lpfr-releases` */
  api: string;
  title: string;
  subtitle: string;
  /** Naziv proizvoda uz verziju („EFIKASA-L"). */
  product: string;
  /** Jedini fajl izdanja. */
  file: string;
  /** Kako se pravi folder izdanja („gradlew release"). */
  buildCommand: string;
  /** Javni link za prvu instalaciju. */
  downloadUrl: string;
  /** Prefiks `data-testid` oznaka. */
  testId: string;
  /** Imenica za uređaje u panelu: zaglavlje kolone, „N ...", naslov sekcije. */
  noun: { one: string; many: string; title: string };
  guideId: string;
  guideTitle: string;
  guide: React.ReactNode;
  criticalWarning: (build: number) => React.ReactNode;
  mandatoryWarning: (build: number) => React.ReactNode;
  /** Upozorenje u dijalogu objave kad release.json već traži kritično / rok. */
  releaseCritical: React.ReactNode;
  releaseMandatory: (rok: string) => React.ReactNode;
  publishedHint: string;
};

const LPFR: ReleasesConfig = {
  api: '/admin/lpfr-releases',
  title: 'L-PFR izdanja',
  subtitle: 'Objava novog izdanja, kritična i obavezna ažuriranja svih L-PFR uređaja',
  product: 'EFIKASA-L',
  file: 'efikasaLpfr-setup.exe',
  buildCommand: 'gradlew release',
  downloadUrl: 'https://api.efikasa.rs/api/lpfr/download',
  testId: 'lpfr',
  noun: { one: 'Uređaj', many: 'uređaja', title: 'Uređaji' },
  guideId: 'lpfr-izdanja',
  guideTitle: 'Kako rade izdanja L-PFR-a - pročitaj pre označavanja kritičnog',
  guide: (
    <>
      <Pojam naziv="Obično izdanje">
        Uređaj ga dobija uz javljanje (na minut) i nudi operateru da ažurira kad mu odgovara.
      </Pojam>
      <Pojam naziv="Kritično">
        Uređaj ispod tog izdanja se <strong>ažurira sam, bez pitanja</strong>. Posle ažuriranja operater
        ponovo unosi PIN kartice. Uvek se nudi najnovije izdanje - ono sadrži i kritičnu ispravku.
      </Pojam>
      <Pojam naziv="Obavezno od">
        Posle tog trenutka L-PFR koji nije ažuriran <strong>ne izdaje račune</strong> (ni bez interneta,
        kad mu stigne nov zakup). Rok uvek povlači i „kritično".
      </Pojam>
      <Pojam naziv="Povučeno">
        Izdanje se više ne nudi i ne preuzima, i ne računa se kao kritično. Uređaji koji su ga već
        instalirali ostaju na njemu do sledećeg izdanja.
      </Pojam>
      <Pojam naziv="Novo izdanje">
        Zamenjuje okačeno - na serveru je uvek samo jedna verzija (isti build se prepisuje, niži se odbija).
        Ako je okačeno izdanje bilo kritično ili obavezno, novo to preuzima. Trag objava je u sysadmin auditu.
      </Pojam>
    </>
  ),
  criticalWarning: (build) => (
    <>
      Svaki L-PFR ispod build-a {build}, kod svih firmi, <strong>ažuriraće se sam, bez pitanja
      operatera</strong>, čim se javi. Posle ažuriranja operater mora <strong>ponovo da unese PIN
      kartice</strong> - dok to ne uradi, uređaj ne izdaje račune. Radite ovo samo za ispravku koja ne sme da čeka.
    </>
  ),
  mandatoryWarning: (build) => (
    <>
      Posle izabranog trenutka L-PFR koji nije ažuriran na build {build} ili noviji
      <strong> NE IZDAJE RAČUNE</strong>, kod svih firmi. Izdanje postaje i kritično (uređaji se do tada
      ažuriraju sami). Uređaj koji je ugašen ili bez interneta do roka ostaće bez izdavanja dok se ne ažurira.
    </>
  ),
  releaseCritical: <>release.json označava izdanje kao <strong>kritično</strong> - uređaji se ažuriraju sami, a operater ponovo unosi PIN kartice.</>,
  releaseMandatory: (rok) => <>release.json traži <strong>obavezno ažuriranje od {rok}</strong> - posle toga neažuriran L-PFR ne izdaje račune.</>,
  publishedHint: 'uređaji ga vide pri sledećem javljanju',
};

export function LpfrReleasesPage() {
  return <ReleasesPage cfg={LPFR} />;
}

export function ReleasesPage({ cfg }: { cfg: ReleasesConfig }) {
  const [pregled, setPregled] = useState<Pregled | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [poruka, setPoruka] = useState<string | null>(null);
  const [potvrda, setPotvrda] = useState<Potvrda | null>(null);
  const [otpremanje, setOtpremanje] = useState(false);
  const isAdmin = loadSession()?.admin.role === 'admin';

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPregled(await api.get<Pregled>(cfg.api));
    } catch (e: any) {
      setError(greska(e, 'Greška'));
    } finally {
      setLoading(false);
    }
  }, [cfg.api]);

  useEffect(() => { void refresh(); }, [refresh]);

  const izmeni = async (build: number, telo: Record<string, unknown>, uspeh: string) => {
    setError(null); setPoruka(null);
    try {
      await api.patch(`${cfg.api}/${build}`, telo);
      setPoruka(uspeh);
      await refresh();
    } catch (e: any) {
      setError(greska(e, 'Izmena nije sačuvana'));
    }
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>{cfg.title}</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            {cfg.subtitle}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => void refresh()} disabled={loading} style={btnSecondary}>
          <RefreshCw size={16} /> Osveži
        </button>
        {isAdmin && (
          <button onClick={() => setOtpremanje(true)} style={btnPrimary} data-testid={`${cfg.testId}-novo-izdanje`}>
            <Upload size={16} /> Novo izdanje
          </button>
        )}
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {poruka && <div style={okBox}><Check size={14} /> {poruka}</div>}

      <Uputstvo id={cfg.guideId} naslov={cfg.guideTitle}>
        {cfg.guide}
      </Uputstvo>

      {pregled && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 16 }}>
          <Brojka naslov={`Aktivnih (${cfg.noun.many})`} vrednost={String(pregled.totalDevices)} />
          <Brojka naslov="Okačena verzija" vrednost={pregled.current ? `${pregled.current.version} · build ${pregled.current.build}` : '-'} />
          <Brojka naslov="Na okačenoj verziji" vrednost={pregled.current ? String(pregled.current.devices) : '-'} />
          <Brojka naslov="Iza kritičnog" vrednost={String(pregled.behindCritical)} upozorenje={pregled.behindCritical > 0} />
        </div>
      )}

      <h2 style={sekcija}>Okačena verzija</h2>
      <div style={{ ...card, padding: 16, marginBottom: 24 }} data-testid={`${cfg.testId}-okacena`}>
        {pregled && !pregled.current && (
          <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
            Nijedna verzija nije okačena - javno preuzimanje vraća 404, a ažuriranje se ne nudi.
          </div>
        )}
        {pregled?.current && (() => {
          const r = pregled.current;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: r.withdrawn ? 0.6 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text-strong)' }}>{cfg.product} {r.version}</span>
                <span style={{ fontFamily: 'monospace', color: 'var(--color-text-muted)' }}>build {r.build}</span>
                {r.withdrawn ? <span style={oznaka('siva')}>povučeno</span> : <span style={oznaka('zelena')}>okačeno</span>}
                {r.critical && <span style={oznaka('crvena')}>kritično</span>}
                {r.mandatoryAfter && <span style={oznaka('crvena')}>obavezno od {vreme(r.mandatoryAfter)}</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)', columnGap: 16, rowGap: 4, fontSize: 13 }}>
                <span style={{ color: 'var(--color-text-muted)' }}>Okačeno</span><span>{vreme(r.publishedAt)}</span>
                {Object.entries(r.files).map(([name, f]) => (
                  <React.Fragment key={name}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Fajl</span>
                    <span>
                      {name} · {mb(f.size)}
                      <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-muted)', wordBreak: 'break-all' }}>SHA-256 {f.sha256}</div>
                    </span>
                  </React.Fragment>
                ))}
                <span style={{ color: 'var(--color-text-muted)' }}>Na njoj ({cfg.noun.many})</span><span className="tabular-nums">{r.devices}</span>
                <span style={{ color: 'var(--color-text-muted)' }}>Javno preuzimanje</span>
                <span><a href={cfg.downloadUrl} style={{ color: 'var(--color-primary, #2563eb)' }}>{cfg.downloadUrl.replace(/^https?:\/\//, '')}</a></span>
                {r.notes && (<><span style={{ color: 'var(--color-text-muted)' }}>Napomena</span><span style={{ whiteSpace: 'pre-wrap' }}>{r.notes}</span></>)}
              </div>
              {isAdmin && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {!r.withdrawn && (r.critical ? (
                    <button style={btnSmall} onClick={() => void izmeni(r.build, { critical: false }, `Izdanje ${r.build} više nije kritično.`)}>
                      Skini kritično
                    </button>
                  ) : (
                    <button style={btnSmall} onClick={() => setPotvrda({ vrsta: 'kriticno', izdanje: r })} data-testid={`kriticno-${r.build}`}>
                      Označi kao kritično
                    </button>
                  ))}
                  {!r.withdrawn && (
                    <button style={btnSmall} onClick={() => setPotvrda({ vrsta: 'obavezno', izdanje: r })} data-testid={`obavezno-${r.build}`}>
                      {r.mandatoryAfter ? 'Promeni rok' : 'Obavezno od datuma'}
                    </button>
                  )}
                  {r.withdrawn ? (
                    <button style={btnSmall} onClick={() => void izmeni(r.build, { withdrawn: false }, `Izdanje ${r.build} je ponovo okačeno.`)}>
                      Vrati
                    </button>
                  ) : (
                    <button style={btnSmall} onClick={() => setPotvrda({ vrsta: 'povuci', izdanje: r })} data-testid={`povuci-${r.build}`}>
                      Povuci
                    </button>
                  )}
                </div>
              )}
              {pregled.releases.length > 1 && (
                <div style={napomena}>
                  Na serveru su još i build {pregled.releases.filter((x) => x.build !== r.build).map((x) => x.build).join(', ')} (iz vremena
                  pre zamene) - brišu se pri sledećoj objavi.
                </div>
              )}
            </div>
          );
        })()}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 16 }}>
        <div>
          <h2 style={sekcija}>Raspodela ({cfg.noun.many})</h2>
          <div style={card}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--color-bg)' }}>
                  <th style={th}>Build</th>
                  <th style={th}>Verzija</th>
                  <th style={{ ...th, textAlign: 'right' }}>Broj</th>
                </tr>
              </thead>
              <tbody>
                {pregled?.distribution.map((d) => (
                  <tr key={`${d.build}|${d.version}`}>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{d.build ?? <span style={{ color: 'var(--color-text-muted)' }}>ne javlja</span>}</td>
                    <td style={td}>{d.version ?? '-'}</td>
                    <td style={{ ...td, textAlign: 'right' }} className="tabular-nums">{d.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 style={sekcija}>{cfg.noun.title} iza kritičnog izdanja ({pregled?.behindCritical ?? 0})</h2>
          <div style={{ ...card, overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 620, borderCollapse: 'collapse' }} data-testid={`${cfg.testId}-lagging`}>
              <thead>
                <tr style={{ background: 'var(--color-bg)' }}>
                  <th style={th}>Firma</th>
                  <th style={th}>{cfg.noun.one}</th>
                  <th style={th}>Build</th>
                  <th style={th}>Poslednje javljanje</th>
                  <th style={th}>Obavezno od</th>
                </tr>
              </thead>
              <tbody>
                {pregled?.lagging.length === 0 && (
                  <tr><td colSpan={5} style={{ ...td, color: 'var(--color-text-muted)' }}>Svi ({cfg.noun.many}) su na kritičnom izdanju ili novijem.</td></tr>
                )}
                {pregled?.lagging.map((u) => (
                  <tr key={u.id}>
                    <td style={td}>{u.tenantName}</td>
                    <td style={td}>{u.name}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{u.build ?? '-'} <span style={{ color: 'var(--color-text-muted)' }}>{u.version ?? ''}</span></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{vreme(u.lastSeenAt)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {u.required ? <span style={oznaka('crvena')}>ne izdaje</span> : vreme(u.mandatoryAfter)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {potvrda && (
        <PotvrdaDialog
          cfg={cfg}
          potvrda={potvrda}
          onZatvori={() => setPotvrda(null)}
          onPotvrdi={async (telo, uspeh) => { setPotvrda(null); await izmeni(potvrda.izdanje.build, telo, uspeh); }}
        />
      )}

      {otpremanje && (
        <OtpremanjeDialog
          cfg={cfg}
          onZatvori={() => setOtpremanje(false)}
          trenutno={pregled?.current ?? null}
          onObjavljeno={(o) => {
            setOtpremanje(false);
            setPoruka(`Build ${o.build} je okačen - ${cfg.publishedHint}.`
              + (o.replaced.length ? ` Obrisano sa servera: build ${o.replaced.join(', ')}.` : '')
              + (o.inheritedCritical ? ' Preuzeto kritično/rok od zamenjenog izdanja - proverite oznake ispod.' : ''));
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function Brojka({ naslov, vrednost, upozorenje }: { naslov: string; vrednost: string; upozorenje?: boolean }) {
  return (
    <div style={{ ...card, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{naslov}</div>
      <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700, color: upozorenje ? '#fca5a5' : 'var(--color-text-strong)' }} className="tabular-nums">{vrednost}</div>
    </div>
  );
}

/** `datetime-local` radi u lokalnom vremenu pregledača, server čuva ISO (UTC). */
function zaPolje(iso: string | null): string {
  const d = iso ? new Date(iso) : new Date(Date.now() + 7 * 86_400_000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function PotvrdaDialog({ cfg, potvrda, onZatvori, onPotvrdi }: {
  cfg: ReleasesConfig;
  potvrda: Potvrda;
  onZatvori: () => void;
  onPotvrdi: (telo: Record<string, unknown>, uspeh: string) => Promise<void>;
}) {
  useEscapeToClose(onZatvori);
  const { izdanje } = potvrda;
  const [rok, setRok] = useState(zaPolje(izdanje.mandatoryAfter));
  const [radi, setRadi] = useState(false);
  const rokUProslosti = potvrda.vrsta === 'obavezno' && new Date(rok).getTime() <= Date.now();

  const naslov = potvrda.vrsta === 'kriticno' ? `Označi build ${izdanje.build} kao kritično`
    : potvrda.vrsta === 'obavezno' ? `Obavezno ažuriranje na build ${izdanje.build}`
      : `Povuci build ${izdanje.build}`;

  const potvrdi = async () => {
    setRadi(true);
    if (potvrda.vrsta === 'kriticno') {
      await onPotvrdi({ critical: true }, `Izdanje ${izdanje.build} je kritično.`);
    } else if (potvrda.vrsta === 'obavezno') {
      await onPotvrdi({ mandatoryAfter: new Date(rok).toISOString() }, `Izdanje ${izdanje.build} je obavezno od ${vreme(new Date(rok).toISOString())}.`);
    } else {
      await onPotvrdi({ withdrawn: true }, `Izdanje ${izdanje.build} je povučeno.`);
    }
  };

  return (
    <div style={overlay}>
      <div style={dialog} role="dialog" aria-modal="true">
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)' }}>{naslov}</h3>
          <button onClick={onZatvori} title="Zatvori" style={iconBtn}><X size={16} /></button>
        </header>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {potvrda.vrsta === 'kriticno' && (
            <div style={upozorenjeBox}>
              <ShieldAlert size={16} style={{ flexShrink: 0 }} />
              <span>{cfg.criticalWarning(izdanje.build)}</span>
            </div>
          )}
          {potvrda.vrsta === 'obavezno' && (
            <>
              <div style={upozorenjeBox}>
                <ShieldAlert size={16} style={{ flexShrink: 0 }} />
                <span>{cfg.mandatoryWarning(izdanje.build)}</span>
              </div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                Obavezno od
                <input type="datetime-local" value={rok} onChange={(e) => setRok(e.target.value)} style={{ ...input, marginTop: 4 }} data-testid="obavezno-rok" />
              </label>
              {rokUProslosti && (
                <div style={{ ...napomena, color: '#fca5a5' }}>
                  Rok je u prošlosti - ispod ovog izdanja se prestaje sa izdavanjem <strong>odmah</strong>.
                </div>
              )}
            </>
          )}
          {potvrda.vrsta === 'povuci' && (
            <div style={napomena}>
              Izdanje se više ne nudi i ne može se preuzeti; ako je bilo kritično ili obavezno, to prestaje
              da važi. Ko ga je već instalirao ostaje na njemu. Povlačenje se može vratiti.
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={onZatvori} style={btnSecondary}>Otkaži</button>
            <button onClick={() => void potvrdi()} disabled={radi || !rok} style={{ ...btnPrimary, opacity: radi ? 0.5 : 1 }} data-testid={`${cfg.testId}-potvrdi`}>
              <Check size={16} /> {radi ? 'Čuvanje…' : 'Potvrdi'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

type RelJson = {
  build: number;
  version?: string;
  notes?: string;
  critical?: boolean;
  mandatoryAfter?: string | null;
  files: Record<string, Fajl>;
};

/** PUT sa napretkom - `fetch` ne javlja napredak slanja, `XMLHttpRequest` da. */
function otpremi(apiPath: string, build: number, name: string, file: File, onNapredak: (poslato: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `/api${apiPath}/${build}/files/${encodeURIComponent(name)}`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    const token = localStorage.getItem('admin-token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => onNapredak(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let poruka = `HTTP ${xhr.status}`;
      try { poruka = JSON.parse(xhr.responseText)?.message || poruka; } catch { /* telo nije JSON (npr. nginx 413) */ }
      reject(new Error(`${name}: ${poruka}`));
    };
    xhr.onerror = () => reject(new Error(`${name}: veza je prekinuta`));
    xhr.send(file);
  });
}

type Objavljeno = { build: number; replaced: number[]; inheritedCritical: boolean };

function OtpremanjeDialog({ cfg, trenutno, onZatvori, onObjavljeno }: {
  cfg: ReleasesConfig;
  trenutno: Izdanje | null;
  onZatvori: () => void;
  onObjavljeno: (o: Objavljeno) => void;
}) {
  const [rel, setRel] = useState<RelJson | null>(null);
  const [fajlovi, setFajlovi] = useState<Record<string, File>>({});
  const [greskaIzbora, setGreskaIzbora] = useState<string | null>(null);
  const [napredak, setNapredak] = useState<Record<string, number>>({});
  const [radi, setRadi] = useState(false);
  const [greskaSlanja, setGreskaSlanja] = useState<string | null>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  useEscapeToClose(onZatvori, !radi);

  useEffect(() => {
    // `webkitdirectory` nije u React tipovima - postavlja se direktno.
    folderRef.current?.setAttribute('webkitdirectory', '');
  }, []);

  const izaberi = async (list: FileList | null) => {
    setGreskaIzbora(null); setGreskaSlanja(null); setNapredak({});
    const sve = Array.from(list ?? []);
    const json = sve.find((f) => f.name === 'release.json');
    if (!json) {
      const izabrano = sve.length ? `Izabrano: ${sve.map((f) => f.name).join(', ')}.` : 'Nije izabran nijedan fajl.';
      setRel(null); setFajlovi({});
      setGreskaIzbora(`Među izabranim fajlovima nema release.json. ${izabrano} Izaberite folder izdanja (build/release/<build>) ili oba fajla iz njega.`);
      return;
    }
    let parsed: RelJson;
    try {
      parsed = JSON.parse(await json.text());
    } catch {
      setRel(null); setGreskaIzbora('release.json nije ispravan JSON.'); return;
    }
    if (!Number.isInteger(parsed.build) || parsed.build < 1 || !parsed.files?.[cfg.file]) {
      setRel(null); setGreskaIzbora(`release.json nema build ili ${cfg.file}.`); return;
    }
    const nepoznati = Object.keys(parsed.files).filter((n) => n !== cfg.file);
    if (nepoznati.length) { setRel(null); setGreskaIzbora(`Nepoznat fajl u release.json: ${nepoznati.join(', ')}.`); return; }
    const izbor: Record<string, File> = {};
    for (const name of Object.keys(parsed.files)) {
      const f = sve.find((x) => x.name === name);
      if (f) izbor[name] = f;
    }
    setRel(parsed);
    setFajlovi(izbor);
  };

  const problemi = rel ? [
    ...(trenutno && rel.build < trenutno.build ? [`okačen je build ${trenutno.build} - novo izdanje mora imati isti ili veći build`] : []),
    ...Object.entries(rel.files).flatMap(([name, f]) => {
      const izabran = fajlovi[name];
      if (!izabran) return [`${name}: nije izabran`];
      if (izabran.size !== f.size) return [`${name}: veličina ${izabran.size} umesto ${f.size} iz release.json`];
      return [];
    }),
  ] : [];

  const objavi = async () => {
    if (!rel) return;
    setRadi(true); setGreskaSlanja(null);
    try {
      for (const [name, file] of Object.entries(fajlovi)) {
        await otpremi(cfg.api, rel.build, name, file, (n) => setNapredak((p) => ({ ...p, [name]: n })));
      }
      const o = await api.post<Objavljeno>(cfg.api, rel);
      onObjavljeno({ build: rel.build, replaced: o?.replaced ?? [], inheritedCritical: !!o?.inheritedCritical });
    } catch (e: any) {
      setGreskaSlanja(greska(e, 'Objava nije uspela'));
    } finally {
      setRadi(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={{ ...dialog, maxWidth: 620 }} role="dialog" aria-modal="true">
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)' }}>Novo izdanje: {cfg.product}</h3>
          <button onClick={onZatvori} disabled={radi} title="Zatvori" style={iconBtn}><X size={16} /></button>
        </header>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={napomena}>
            Izaberite folder izdanja koji je napravio <code>{cfg.buildCommand}</code> (ili fajlove <code>release.json</code> i
            <code>{cfg.file}</code>). Server proverava SHA-256 i veličinu; izdanje postaje vidljivo tek kad
            sve prođe i <strong>zamenjuje okačeno</strong>{trenutno ? <> (build {trenutno.build})</> : null}.
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={btnSecondary}>
              Izaberi folder
              <input ref={folderRef} type="file" onChange={(e) => void izaberi(e.target.files)} style={{ display: 'none' }} disabled={radi} />
            </label>
            <label style={btnSecondary}>
              Izaberi fajlove
              <input type="file" multiple onChange={(e) => void izaberi(e.target.files)} style={{ display: 'none' }} disabled={radi} data-testid={`${cfg.testId}-fajlovi`} />
            </label>
          </div>

          {greskaIzbora && <div style={errorBox}><AlertTriangle size={14} /> {greskaIzbora}</div>}

          {rel && (
            <div style={{ ...card, padding: 14, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div><strong>Build {rel.build}</strong> · verzija {rel.version ?? rel.build}</div>
              {trenutno && rel.build === trenutno.build && (
                <div style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                  Isti build kao okačeni - fajl se prepisuje. Ko već ima build {rel.build} ga ne preuzima ponovo;
                  za ispravku koju moraju da dobiju povećajte build.
                </div>
              )}
              {rel.notes && <div style={{ color: 'var(--color-text-muted)', whiteSpace: 'pre-wrap' }}>{rel.notes}</div>}
              {(rel.critical || rel.mandatoryAfter) && (
                <div style={upozorenjeBox}>
                  <ShieldAlert size={16} style={{ flexShrink: 0 }} />
                  <span>
                    {rel.mandatoryAfter ? cfg.releaseMandatory(vreme(rel.mandatoryAfter)) : cfg.releaseCritical}
                  </span>
                </div>
              )}
              {Object.entries(rel.files).map(([name, f]) => {
                const poslato = napredak[name] ?? 0;
                return (
                  <div key={name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span>{name}</span>
                      <span style={{ color: fajlovi[name] ? 'var(--color-text-muted)' : '#fca5a5' }} className="tabular-nums">
                        {fajlovi[name] ? `${mb(poslato)} / ${mb(f.size)}` : 'nije izabran'}
                      </span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--color-bg)', marginTop: 4 }}>
                      <div style={{ height: 4, borderRadius: 2, width: `${f.size ? Math.min(100, (poslato / f.size) * 100) : 0}%`, background: 'var(--color-primary, #2563eb)' }} />
                    </div>
                  </div>
                );
              })}
              {problemi.length > 0 && <div style={{ color: '#fca5a5', fontSize: 12 }}>{problemi.join('; ')}</div>}
            </div>
          )}

          {greskaSlanja && <div style={errorBox}><AlertTriangle size={14} /> {greskaSlanja}</div>}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onZatvori} disabled={radi} style={btnSecondary}>Otkaži</button>
            <button
              onClick={() => void objavi()}
              disabled={radi || !rel || problemi.length > 0}
              style={{ ...btnPrimary, opacity: radi || !rel || problemi.length > 0 ? 0.5 : 1 }}
              data-testid={`${cfg.testId}-objavi`}
            >
              <Upload size={16} /> {radi ? 'Otpremanje…' : 'Otpremi i objavi'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const oznaka = (boja: 'crvena' | 'zelena' | 'siva'): React.CSSProperties => ({
  fontSize: 10, padding: '2px 7px', borderRadius: 999, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap',
  background: boja === 'crvena' ? 'rgba(239,68,68,0.15)' : boja === 'zelena' ? 'rgba(34,197,94,0.15)' : 'var(--color-bg)',
  color: boja === 'crvena' ? '#fca5a5' : boja === 'zelena' ? '#86efac' : 'var(--color-text-muted)',
});

const sekcija: React.CSSProperties = { margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' };
const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', overflow: 'hidden' };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top' };
// Dugmad po design-system § 10 (primarno: px-4 py-2, font-medium, ikona 16).
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 'var(--radius-md, 6px)', background: 'var(--color-primary, #2563eb)', color: '#fff', border: 'none', fontSize: 'var(--font-size-body, 14px)', fontWeight: 500, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 'var(--radius-md, 6px)', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 'var(--font-size-meta, 13px)', fontWeight: 500, cursor: 'pointer' };
const btnSmall: React.CSSProperties = { ...btnSecondary, padding: '4px 10px', fontSize: 12, whiteSpace: 'nowrap' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 4, borderRadius: 4, cursor: 'pointer' };
const input: React.CSSProperties = { display: 'block', width: '100%', padding: '8px 10px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 14 };
const okBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac', fontSize: 13, marginBottom: 14 };
const upozorenjeBox: React.CSSProperties = { display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 6, background: 'rgba(245,158,11,0.12)', color: '#fcd34d', fontSize: 13, lineHeight: 1.5 };
const napomena: React.CSSProperties = { padding: 10, borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text-muted)', fontSize: 12, lineHeight: 1.5 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 520, background: 'var(--color-bg-surface)', borderRadius: 12, border: '1px solid var(--color-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' };
